import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import styles from './AdminDashboardPage.module.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'companies', label: 'Companies' },
  { id: 'users', label: 'Users' },
  { id: 'billing', label: 'Billing' },
  { id: 'requests', label: 'Requests' },
  { id: 'errors', label: 'Errors' },
  { id: 'activity', label: 'Activity' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  } catch {
    return '—';
  }
};

const formatDateShort = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};

const formatMoney = (n) =>
  typeof n === 'number' || (typeof n === 'string' && n !== '')
    ? `R ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const formatNum = (n) =>
  n == null ? '—' : Number(n).toLocaleString();

const statusBadge = (status) => {
  if (!status) return <span className={`${styles.badge} ${styles.badgeMuted}`}>none</span>;
  const ok = ['active', 'trialing'];
  const cls = ok.includes(status) ? styles.badgeOk : styles.badgeWarn;
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  // #root has `align-items: center; height: 100%` (index.css), so .wrapper's
  // dark background only paints to the first viewport's height. Force the
  // body bg dark while the admin is mounted so scrolling reveals more dark
  // background, not the global white. Restored on unmount.
  useEffect(() => {
    const prevBg = document.body.style.background;
    document.body.style.background = '#0b1424';
    return () => { document.body.style.background = prevBg; };
  }, []);

  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [signups, setSignups] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [billing, setBilling] = useState([]);
  const [requests, setRequests] = useState(null);
  const [requestsDays, setRequestsDays] = useState(7);
  const [errors, setErrors] = useState(null);
  const [errorsDays, setErrorsDays] = useState(7);
  const [errorsFilter, setErrorsFilter] = useState(null);
  const [activity, setActivity] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [companyRequests, setCompanyRequests] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadTab = useCallback(async (current) => {
    setLoading(true);
    setError('');
    try {
      if (current === 'overview') {
        const [ov, sg] = await Promise.all([
          adminService.getOverview(),
          adminService.getSignups(30),
        ]);
        setOverview(ov);
        setSignups(sg);
      } else if (current === 'companies') {
        setCompanies((await adminService.getCompanies()).companies);
      } else if (current === 'users') {
        setUsers((await adminService.getUsers()).users);
      } else if (current === 'billing') {
        setBilling((await adminService.getBilling()).subscriptions);
      } else if (current === 'requests') {
        setRequests(await adminService.getRequests(requestsDays));
      } else if (current === 'errors') {
        setErrors(await adminService.getErrors(errorsDays, errorsFilter));
      } else if (current === 'activity') {
        setActivity((await adminService.getActivity()).activity);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [requestsDays, errorsDays, errorsFilter]);

  useEffect(() => {
    if (!adminService.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    loadTab(tab);
  }, [tab, navigate, loadTab]);

  const openCompany = async (companyId, companyName) => {
    setSelectedCompany({ id: companyId, name: companyName });
    setCompanyDetail(null);
    setCompanyRequests(null);
    setLoading(true);
    setError('');
    try {
      const [detail, reqs] = await Promise.all([
        adminService.getCompanyDetail(companyId),
        adminService.getCompanyRequests(companyId, 7),
      ]);
      setCompanyDetail(detail);
      setCompanyRequests(reqs);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const closeCompany = () => {
    setSelectedCompany(null);
    setCompanyDetail(null);
    setCompanyRequests(null);
  };

  const handleCompanyDeleted = (companyId) => {
    setCompanies((prev) => prev.filter((c) => c.company_id !== companyId));
  };

  const handleLogout = () => {
    adminService.logout();
    navigate('/admin/login');
  };

  if (selectedCompany) {
    return (
      <CompanyDetailView
        company={selectedCompany}
        detail={companyDetail}
        requests={companyRequests}
        loading={loading}
        error={error}
        onClose={closeCompany}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1 className={styles.title}>Nodel Admin</h1>
        <button className={styles.logout} onClick={handleLogout}>Log out</button>
      </header>

      <div className={styles.content}>
        <div className={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {loading && <div className={styles.loading}>Loading…</div>}

        {!loading && tab === 'overview' && overview && (
          <OverviewTab overview={overview} signups={signups} />
        )}

        {!loading && tab === 'companies' && (
          <CompaniesTab companies={companies} onOpen={openCompany} onDelete={handleCompanyDeleted} />
        )}

        {!loading && tab === 'users' && <UsersTab users={users} />}

        {!loading && tab === 'billing' && (
          <BillingTab
            billing={billing}
            onResynced={(companyId, amount) =>
              setBilling((prev) =>
                prev.map((b) =>
                  b.company_id === companyId ? { ...b, last_resync_amount: amount } : b
                )
              )
            }
          />
        )}

        {!loading && tab === 'requests' && requests && (
          <RequestsTab
            data={requests}
            days={requestsDays}
            onDaysChange={(d) => { setRequestsDays(d); }}
            onRefresh={() => loadTab('requests')}
          />
        )}

        {!loading && tab === 'errors' && errors && (
          <ErrorsTab
            data={errors}
            days={errorsDays}
            filter={errorsFilter}
            onDaysChange={(d) => setErrorsDays(d)}
            onFilterChange={(f) => setErrorsFilter(f)}
            onRefresh={() => loadTab('errors')}
          />
        )}

        {!loading && tab === 'activity' && <ActivityTab activity={activity} />}
      </div>
    </div>
  );
}

/* ---------- Overview ---------- */

function OverviewTab({ overview, signups }) {
  const subs = overview.subscriptions || {};
  const subDistItems = [
    { label: 'Active', value: Number(subs.active) || 0, color: '#10B981' },
    { label: 'Trialing', value: Number(subs.trialing) || 0, color: '#3B82F6' },
    { label: 'Past due', value: Number(subs.past_due) || 0, color: '#EF4444' },
    { label: 'Pending cancel', value: Number(subs.pending_cancel) || 0, color: '#F59E0B' },
    { label: 'Cancelled', value: Number(subs.canceled) || 0, color: '#64748B' },
  ].filter((x) => x.value > 0);

  const projectDistItems = [
    {
      label: 'Active',
      value: Math.max(0, (Number(overview.active_projects) || 0) - (Number(overview.overdue_projects) || 0)),
      color: '#10B981',
    },
    { label: 'Overdue', value: Number(overview.overdue_projects) || 0, color: '#EF4444' },
    {
      label: 'Completed / other',
      value: Math.max(0, (Number(overview.projects_total) || 0) - (Number(overview.active_projects) || 0)),
      color: '#64748B',
    },
  ].filter((x) => x.value > 0);

  return (
    <>
      <SectionTitle>Customers</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Companies" value={formatNum(overview.companies)} sub={`+${overview.new_companies_7d ?? 0} this week`} />
        <Metric label="Users" value={formatNum(overview.users)} sub={`+${overview.new_users_7d ?? 0} this week`} />
        <Metric label="Active users (7d)" value={formatNum(overview.active_users_7d)} />
        <Metric label="Unverified users" value={formatNum(overview.unverified_users)} />
      </div>

      <SectionTitle>Subscriptions</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Active" value={formatNum(subs.active)} />
        <Metric label="Trialing" value={formatNum(subs.trialing)} />
        <Metric label="Past due" value={formatNum(subs.past_due)} warn={(subs.past_due ?? 0) > 0} />
        <Metric label="Cancelled" value={formatNum(subs.canceled)} />
        <Metric label="Pending cancel" value={formatNum(subs.pending_cancel)} warn={(subs.pending_cancel ?? 0) > 0} />
        <Metric label="MRR estimate" value={formatMoney(overview.mrr_estimate)} />
      </div>

      <SectionTitle>Activity</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Active projects" value={formatNum(overview.active_projects)} sub={`${formatNum(overview.projects_total)} total`} />
        <Metric label="Overdue projects" value={formatNum(overview.overdue_projects)} warn={(overview.overdue_projects ?? 0) > 0} />
        <Metric label="Open leads" value={formatNum(overview.open_leads)} sub={`${formatNum(overview.leads_total)} total`} />
        <Metric label="Timesheet hrs (30d)" value={formatNum(overview.timesheet_hours_30d)} />
        <Metric label="Bookings total" value={formatNum(overview.bookings_total)} />
        <Metric label="Pending time-off" value={formatNum(overview.pending_time_off)} warn={(overview.pending_time_off ?? 0) > 0} />
        <Metric label="Notifications (30d)" value={formatNum(overview.notifications_30d)} />
      </div>

      {signups && signups.series && (
        <>
          <SectionTitle>Signups — last {signups.days} days</SectionTitle>
          <div className={styles.twoCol}>
            <Card title="Daily signups">
              <SignupsChart series={signups.series} />
            </Card>
            <Card title="Cumulative user growth">
              <CumulativeSignupsChart series={signups.series} />
            </Card>
          </div>
        </>
      )}

      <SectionTitle>Distributions</SectionTitle>
      <div className={styles.twoCol}>
        <Card title="Subscription mix">
          <Donut items={subDistItems} centerLabel={formatNum(subDistItems.reduce((s, x) => s + x.value, 0))} centerSub="subs" />
        </Card>
        <Card title="Project health">
          <Donut items={projectDistItems} centerLabel={formatNum(overview.projects_total)} centerSub="projects" />
        </Card>
      </div>
    </>
  );
}

function CumulativeSignupsChart({ series }) {
  if (!series.length) return <div className={styles.empty}>No signups yet.</div>;
  let runningUsers = 0;
  let runningCompanies = 0;
  const enriched = series.map((d) => {
    runningUsers += Number(d.users) || 0;
    runningCompanies += Number(d.companies) || 0;
    return { ...d, cum_users: runningUsers, cum_companies: runningCompanies };
  });
  const max = Math.max(1, ...enriched.map((d) => d.cum_users));
  return (
    <StackedTimeline
      series={enriched}
      max={max}
      stacks={[
        { key: 'cum_companies', label: 'Companies (cumulative)', color: '#10B981', value: (d) => d.cum_companies },
        { key: 'cum_users_only', label: 'Users (cumulative)', color: '#3B82F6', value: (d) => Math.max(0, d.cum_users - d.cum_companies) },
      ]}
      tooltip={(d) => `${d.date} · ${d.cum_users} users · ${d.cum_companies} companies`}
    />
  );
}

function SignupsChart({ series }) {
  if (!series.length) return <div className={styles.empty}>No signups yet.</div>;
  // A new company implies a user signup, so users >= companies. Stack them on a
  // shared scale: green = new companies, blue = users in existing companies.
  const max = Math.max(1, ...series.map((d) => d.users));
  return (
    <StackedTimeline
      series={series}
      max={max}
      stacks={[
        {
          key: 'companies',
          label: 'New companies',
          color: '#10B981',
          value: (d) => Number(d.companies) || 0,
        },
        {
          key: 'usersOnly',
          label: 'User signups',
          color: '#3B82F6',
          value: (d) => Math.max(0, (Number(d.users) || 0) - (Number(d.companies) || 0)),
        },
      ]}
      tooltip={(d) => `${d.date} · ${d.users} users · ${d.companies} new companies`}
    />
  );
}

/* ---------- Reusable charts ---------- */

// Reusable stacked-bar timeline. Renders y-axis gridlines + tick labels, a
// legend, and per-bar hover tooltip. `stacks` are drawn bottom-up.
function StackedTimeline({ series, max, stacks, tooltip, height = 160 }) {
  const [hover, setHover] = useState(null);
  const yMax = Math.max(1, max);
  const ticks = yAxisTicks(yMax, 4);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartLegend}>
        {stacks.map((s) => (
          <span key={s.key}>
            <span className={styles.legendDot} style={{ background: s.color }} />
            {' '}{s.label}
          </span>
        ))}
      </div>

      <div className={styles.chartArea} style={{ height }}>
        <div className={styles.chartGrid}>
          {ticks.map((t) => (
            <div key={t} className={styles.chartGridRow}>
              <span className={styles.chartGridLabel}>{formatNum(t)}</span>
              <span className={styles.chartGridLine} />
            </div>
          ))}
        </div>

        <div className={styles.chartBars}>
          {series.map((d, idx) => {
            const total = stacks.reduce((acc, s) => acc + s.value(d), 0);
            return (
              <div
                key={d.date || idx}
                className={styles.chartCol}
                onMouseEnter={() => setHover({ d, total })}
                onMouseLeave={() => setHover(null)}
              >
                <div className={styles.chartBarStack}>
                  {stacks.map((s) => {
                    const v = s.value(d);
                    if (v <= 0) return null;
                    return (
                      <div
                        key={s.key}
                        className={styles.chartBar}
                        style={{
                          height: `${(v / yMax) * 100}%`,
                          background: s.color,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {hover && (
          <div className={styles.chartTooltip}>
            {tooltip ? tooltip(hover.d, hover.total) : `${hover.d.date}: ${formatNum(hover.total)}`}
          </div>
        )}
      </div>

      <div className={styles.chartXAxis}>
        <span>{series[0]?.date}</span>
        <span>{series[Math.floor(series.length / 2)]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

// Nice round y-axis ticks. Returns descending values from top to 0.
function yAxisTicks(max, count) {
  const niceMax = niceNumber(max);
  const step = niceMax / count;
  const out = [];
  for (let i = count; i >= 0; i--) out.push(Math.round(step * i));
  return out;
}
function niceNumber(n) {
  if (n <= 0) return 1;
  const exp = Math.floor(Math.log10(n));
  const f = n / Math.pow(10, exp);
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * Math.pow(10, exp);
}

// Donut chart for distribution data. items = [{ label, value, color }].
function Donut({ items, size = 160, thickness = 26, centerLabel, centerSub }) {
  const total = items.reduce((s, i) => s + (i.value || 0), 0);
  if (total === 0) return <div className={styles.empty}>No data yet.</div>;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className={styles.donutWrap}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#0e1726" strokeWidth={thickness} fill="none"
        />
        {items.map((it) => {
          const frac = (it.value || 0) / total;
          const dash = frac * c;
          const seg = (
            <circle
              key={it.label}
              cx={size / 2} cy={size / 2} r={r}
              stroke={it.color}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += dash;
          return seg;
        })}
        <text
          x="50%" y="50%" textAnchor="middle" dy="-2"
          fontSize="22" fontWeight="700" fill="#f1f5f9"
        >
          {centerLabel != null ? centerLabel : formatNum(total)}
        </text>
        {centerSub && (
          <text x="50%" y="50%" textAnchor="middle" dy="18" fontSize="10" fill="#94a3b8" letterSpacing="0.06em">
            {centerSub}
          </text>
        )}
      </svg>
      <div className={styles.donutLegend}>
        {items.map((it) => (
          <div key={it.label} className={styles.donutLegendRow}>
            <span className={styles.legendDot} style={{ background: it.color }} />
            <span style={{ color: '#cbd5e1', flex: 1 }}>{it.label}</span>
            <span style={{ color: '#f1f5f9', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {formatNum(it.value)}
            </span>
            <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'right' }}>
              {((it.value / total) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  // 4xx — warm yellows/oranges/reds, scaled by severity
  400: '#fbbf24', 401: '#f59e0b', 403: '#ea580c', 404: '#fb923c',
  409: '#f97316', 422: '#facc15', 429: '#fb7185',
  // 5xx — deep reds
  500: '#dc2626', 502: '#b91c1c', 503: '#991b1b', 504: '#7f1d1d',
  0: '#64748b', // legacy "unknown" code
};
function statusColor(code) {
  if (STATUS_COLORS[code]) return STATUS_COLORS[code];
  const klass = Math.floor(code / 100);
  if (klass === 5) return '#dc2626';
  if (klass === 4) return '#f59e0b';
  return '#64748b';
}
function statusLabel(code) {
  if (code === 0) return 'unknown (legacy)';
  return String(code);
}

/* ---------- Companies ---------- */

function CompaniesTab({ companies, onOpen, onDelete }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    if (!query) return companies;
    const q = query.toLowerCase();
    return companies.filter((c) => c.name?.toLowerCase().includes(q));
  }, [companies, query]);

  return (
    <>
      <input
        className={styles.search}
        placeholder="Search companies…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Table
        columns={['Company', 'Plan', 'Status', 'Users', 'Projects', 'Leads', 'Created', '', '']}
        rows={filtered.map((c) => [
          c.name,
          c.plan_name || '—',
          statusBadge(c.subscription_status),
          <>
            {c.user_count}
            {c.unverified_count > 0 && (
              <span className={`${styles.badge} ${styles.badgeWarn} ${styles.inlineBadge}`}>
                {c.unverified_count} unverified
              </span>
            )}
          </>,
          c.project_count,
          c.lead_count,
          formatDate(c.created_at),
          <button className={styles.linkBtn} onClick={() => onOpen(c.company_id, c.name)}>
            View →
          </button>,
          <DeleteCompanyButton key={`del-${c.company_id}`} company={c} onDeleted={onDelete} />,
        ])}
      />
    </>
  );
}

function DeleteCompanyButton({ company, onDeleted }) {
  const [state, setState] = useState({ busy: false, error: null });

  const handleClick = async () => {
    const label = company.name || `company ${company.company_id}`;
    const confirmed = window.prompt(
      `This permanently deletes "${label}" and ALL of its data ` +
      `(${company.user_count} users, ${company.project_count} projects, ${company.lead_count} leads, ` +
      `timesheets, bookings, billing — everything). This cannot be undone.\n\n` +
      `Type the company name to confirm:`
    );
    if (confirmed === null) return; // cancelled
    if (confirmed.trim().toLowerCase() !== (company.name || '').trim().toLowerCase()) {
      setState({ busy: false, error: 'Name did not match — nothing deleted.' });
      return;
    }

    setState({ busy: true, error: null });
    try {
      await adminService.deleteCompany(company.company_id);
      onDeleted?.(company.company_id);
    } catch (err) {
      setState({ busy: false, error: err.response?.data?.error || 'Delete failed' });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        className={styles.linkBtn}
        style={{ color: '#ef4444' }}
        onClick={handleClick}
        disabled={state.busy}
      >
        {state.busy ? 'Deleting…' : 'Delete'}
      </button>
      {state.error && <span style={{ color: '#ef4444', fontSize: 11 }}>{state.error}</span>}
    </div>
  );
}

/* ---------- Users ---------- */

function UsersTab({ users }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter(
      (u) => u.email?.toLowerCase().includes(q) || u.company_name?.toLowerCase().includes(q)
    );
  }, [users, query]);

  return (
    <>
      <input
        className={styles.search}
        placeholder="Search by email or company…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Table
        columns={['Email', 'Name', 'Company', 'Title', 'Verified', 'Created', '']}
        rows={filtered.map((u) => [
          u.email,
          u.employee_name || '—',
          u.company_name || `#${u.company_id}`,
          u.job_title || '—',
          u.is_verified
            ? <span className={`${styles.badge} ${styles.badgeOk}`}>yes</span>
            : <span className={`${styles.badge} ${styles.badgeWarn}`}>no</span>,
          formatDate(u.created_at),
          u.is_verified
            ? <span style={{ color: '#64748b', fontSize: 12 }}>—</span>
            : <ResendVerificationButton key={`resend-${u.user_id}`} user={u} />,
        ])}
      />
    </>
  );
}

function ResendVerificationButton({ user }) {
  const [state, setState] = useState({ busy: false, message: null, error: null });

  const handleClick = async () => {
    setState({ busy: true, message: null, error: null });
    try {
      const result = await adminService.resendUserVerification(user.user_id);
      setState({ busy: false, message: result.message || 'Sent', error: null });
    } catch (err) {
      setState({
        busy: false,
        message: null,
        error: err.response?.data?.error || 'Send failed',
      });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button className={styles.linkBtn} onClick={handleClick} disabled={state.busy}>
        {state.busy ? 'Sending…' : 'Resend verification'}
      </button>
      {state.message && <span style={{ color: '#22c55e', fontSize: 11 }}>{state.message}</span>}
      {state.error && <span style={{ color: '#ef4444', fontSize: 11 }}>{state.error}</span>}
    </div>
  );
}

/* ---------- Billing ---------- */

function BillingTab({ billing, onResynced }) {
  return (
    <Table
      columns={['Company', 'Plan', 'Monthly', 'Users', 'Resources', 'Status', 'Trial ends', 'Period end', 'PayFast', 'Cancel at end', 'Resync']}
      rows={billing.map((b) => [
        b.company_name,
        b.plan_display_name || b.plan_name || '—',
        b.monthly_price != null ? formatMoney(b.monthly_price) : '—',
        formatNum(b.billable_users),
        formatNum(b.billable_resources),
        statusBadge(b.status),
        formatDate(b.trial_ends_at),
        formatDate(b.current_period_end),
        b.has_payfast_token ? 'yes' : 'no',
        b.cancel_at_period_end ? 'yes' : 'no',
        <ResyncPayfastButton
          key={`resync-${b.company_id}`}
          company={b}
          onResynced={onResynced}
        />,
      ])}
    />
  );
}

function ResyncPayfastButton({ company, onResynced }) {
  const [state, setState] = useState({ busy: false, message: null, error: null });

  if (company.status !== 'active' || !company.has_payfast_token) {
    return <span style={{ color: '#64748b', fontSize: 12 }}>—</span>;
  }

  const handleClick = async () => {
    const label = company.company_name || `company ${company.company_id}`;
    if (!window.confirm(
      `Push the current calculated amount to PayFast for ${label}? ` +
      `This sets the recurring_amount on PayFast — the next renewal will bill the new value.`
    )) return;

    setState({ busy: true, message: null, error: null });
    try {
      const result = await adminService.resyncCompanyPayfast(company.company_id);
      setState({ busy: false, message: `Pushed R${result.amount}`, error: null });
      onResynced?.(company.company_id, result.amount);
    } catch (err) {
      setState({
        busy: false,
        message: null,
        error: err.response?.data?.error || 'Resync failed',
      });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button className={styles.linkBtn} onClick={handleClick} disabled={state.busy}>
        {state.busy ? 'Pushing…' : 'Resync PayFast'}
      </button>
      {state.message && <span style={{ color: '#22c55e', fontSize: 11 }}>{state.message}</span>}
      {state.error && <span style={{ color: '#ef4444', fontSize: 11 }}>{state.error}</span>}
    </div>
  );
}

/* ---------- Requests (global) ---------- */

function RequestsTab({ data, days, onDaysChange, onRefresh }) {
  const totals = data.totals || {};
  return (
    <>
      <div className={styles.controls}>
        <label className={styles.controlLabel}>Window</label>
        <select
          className={styles.select}
          value={days}
          onChange={(e) => { onDaysChange(Number(e.target.value)); setTimeout(onRefresh, 0); }}
        >
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className={styles.metricsGrid}>
        <Metric label="Total requests" value={formatNum(totals.total)} />
        <Metric label="4xx errors" value={formatNum(totals.errors_4xx)} warn={(totals.errors_4xx ?? 0) > 0} />
        <Metric label="5xx errors" value={formatNum(totals.errors_5xx)} warn={(totals.errors_5xx ?? 0) > 0} />
        <Metric label="Avg latency (ms)" value={totals.avg_ms ?? '—'} />
        <Metric label="Active companies" value={formatNum(totals.active_companies)} />
      </div>

      <SectionTitle>Requests by day</SectionTitle>
      <RequestsTimeline series={data.by_day || []} />

      <SectionTitle>Top companies by request volume</SectionTitle>
      <Table
        columns={['Company', 'Requests', '4xx', '5xx']}
        rows={(data.by_company || []).map((r) => [
          r.company_name || (r.company_id === 0 ? '— unauthenticated —' : `#${r.company_id}`),
          formatNum(r.count),
          formatNum(r.errors_4xx),
          formatNum(r.errors_5xx),
        ])}
      />

      <SectionTitle>Top endpoints</SectionTitle>
      <Table
        columns={['Method', 'Route', 'Requests', 'Avg ms']}
        rows={(data.by_endpoint || []).map((r) => [r.method, r.route, formatNum(r.count), r.avg_ms ?? '—'])}
      />
    </>
  );
}

function RequestsTimeline({ series }) {
  if (!series.length) return <div className={styles.loading}>No data yet.</div>;
  const max = Math.max(1, ...series.map((d) => Number(d.count)));
  return (
    <StackedTimeline
      series={series}
      max={max}
      stacks={[
        {
          key: 'ok',
          label: 'Successful',
          color: '#3B82F6',
          value: (d) => Math.max(0, (Number(d.count) || 0) - (Number(d.errors) || 0)),
        },
        {
          key: 'errors',
          label: 'Errors (4xx + 5xx)',
          color: '#EF4444',
          value: (d) => Number(d.errors) || 0,
        },
      ]}
      tooltip={(d) => `${d.date} · ${formatNum(d.count)} requests · ${formatNum(d.errors)} errors`}
    />
  );
}

/* ---------- Errors ---------- */

function ErrorsTab({ data, days, filter, onDaysChange, onFilterChange, onRefresh }) {
  const t = data.totals || {};
  const totalReq = Number(t.total_requests) || 0;
  const totalErr = Number(t.total_errors) || 0;
  const errRate = totalReq > 0 ? (totalErr / totalReq) * 100 : 0;

  const byCode = (data.by_code || []).map((r) => ({
    label: `${statusLabel(r.status_code)} · ${r.status_class === 5 ? 'server' : 'client'}`,
    value: Number(r.count) || 0,
    color: statusColor(Number(r.status_code) || (r.status_class * 100)),
  }));

  const timeline = data.timeline || [];
  const timelineMax = Math.max(1, ...timeline.map((d) => (Number(d.errors_4xx) + Number(d.errors_5xx))));

  const setFilter = (f) => { onFilterChange(f); setTimeout(onRefresh, 0); };

  return (
    <>
      <div className={styles.controls}>
        <label className={styles.controlLabel}>Window</label>
        <select
          className={styles.select}
          value={days}
          onChange={(e) => { onDaysChange(Number(e.target.value)); setTimeout(onRefresh, 0); }}
        >
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>

        <div style={{ display: 'inline-flex', gap: 6, marginLeft: 12 }}>
          <FilterChip active={filter === null} onClick={() => setFilter(null)}>All errors</FilterChip>
          <FilterChip active={filter === '4'} onClick={() => setFilter('4')} accent="#f59e0b">4xx</FilterChip>
          <FilterChip active={filter === '5'} onClick={() => setFilter('5')} accent="#dc2626">5xx</FilterChip>
        </div>
      </div>

      <div className={styles.metricsGrid}>
        <Metric label="Total errors" value={formatNum(totalErr)} warn={totalErr > 0} sub={`${errRate.toFixed(2)}% of all requests`} />
        <Metric label="4xx (client)" value={formatNum(t.total_4xx)} sub="bad request, auth, validation" />
        <Metric label="5xx (server)" value={formatNum(t.total_5xx)} warn={Number(t.total_5xx) > 0} sub="our bugs / infra" />
        <Metric label="Total requests" value={formatNum(totalReq)} sub={`over last ${days}d`} />
      </div>

      <div className={styles.twoCol}>
        <Card title="Status code distribution">
          <Donut items={byCode} centerLabel={formatNum(totalErr)} centerSub="errors" />
        </Card>
        <Card title={`Errors per day · last ${days}d`}>
          <StackedTimeline
            series={timeline}
            max={timelineMax}
            stacks={[
              { key: '4xx', label: '4xx (client)', color: '#F59E0B', value: (d) => Number(d.errors_4xx) || 0 },
              { key: '5xx', label: '5xx (server)', color: '#DC2626', value: (d) => Number(d.errors_5xx) || 0 },
            ]}
            tooltip={(d) => `${d.date} · ${formatNum(d.errors_4xx)} × 4xx · ${formatNum(d.errors_5xx)} × 5xx`}
            height={140}
          />
        </Card>
      </div>

      <SectionTitle>Top error-producing endpoints</SectionTitle>
      <Table
        columns={['Method', 'Route', 'Status', 'Class', 'Errors', 'Avg ms']}
        rows={(data.by_endpoint || []).map((r) => [
          <span className={styles.methodPill}>{r.method}</span>,
          <code style={{ fontSize: 12, color: '#cbd5e1' }}>{r.route}</code>,
          <span
            className={styles.codePill}
            style={{ background: statusColor(Number(r.status_code) || r.status_class * 100) + '22', color: statusColor(Number(r.status_code) || r.status_class * 100) }}
          >
            {statusLabel(r.status_code)}
          </span>,
          r.status_class === 5 ? 'server' : 'client',
          formatNum(r.count),
          r.avg_ms ?? '—',
        ])}
      />

      <SectionTitle>Top error-producing companies</SectionTitle>
      <Table
        columns={['Company', '4xx', '5xx', 'Total errors']}
        rows={(data.by_company || []).map((r) => [
          r.company_name,
          formatNum(r.errors_4xx),
          formatNum(r.errors_5xx),
          formatNum(r.total_errors),
        ])}
      />

      <SectionTitle>Slowest endpoints (avg latency)</SectionTitle>
      <Table
        columns={['Method', 'Route', 'Avg ms', 'Requests']}
        rows={(data.slowest_endpoints || []).map((r) => [
          <span className={styles.methodPill}>{r.method}</span>,
          <code style={{ fontSize: 12, color: '#cbd5e1' }}>{r.route}</code>,
          <span style={{ color: r.avg_ms > 1000 ? '#ef4444' : r.avg_ms > 500 ? '#f59e0b' : '#cbd5e1', fontWeight: 600 }}>
            {r.avg_ms ?? '—'}
          </span>,
          formatNum(r.count),
        ])}
      />
    </>
  );
}

function FilterChip({ active, onClick, children, accent = '#3b82f6' }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', borderRadius: 999,
        border: `1px solid ${active ? accent : '#2d3d5e'}`,
        background: active ? accent + '22' : 'transparent',
        color: active ? accent : '#94a3b8',
        fontSize: 12, fontWeight: 600, cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </button>
  );
}

/* ---------- Activity ---------- */

function ActivityTab({ activity }) {
  if (!activity.length) return <div className={styles.loading}>No recent activity.</div>;
  return (
    <Table
      columns={['When', 'Type', 'Subject', 'Company', 'Detail']}
      rows={activity.map((a) => [
        formatDate(a.ts),
        <span className={`${styles.badge} ${styles.badgeMuted}`}>{a.kind.replace(/_/g, ' ')}</span>,
        a.subject || '—',
        a.company || '—',
        a.detail || '—',
      ])}
    />
  );
}

/* ---------- Company Detail ---------- */

function CompanyDetailView({ company, detail, requests, loading, error, onClose, onLogout }) {
  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={onClose}>← Back</button>
          <h1 className={styles.title}>{company.name}</h1>
        </div>
        <button className={styles.logout} onClick={onLogout}>Log out</button>
      </header>

      <div className={styles.content}>
        {error && <div className={styles.error}>{error}</div>}
        {loading && <div className={styles.loading}>Loading…</div>}

        {!loading && detail && (
          <CompanyDetailBody detail={detail} requests={requests} />
        )}
      </div>
    </div>
  );
}

function CompanyDetailBody({ detail, requests }) {
  const c = detail.company || {};
  return (
    <>
      {/* Snapshot */}
      <SectionTitle>Snapshot</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Created" value={formatDateShort(c.created_at)} />
        <Metric label="Plan" value={c.plan_display_name || c.plan_name || '—'} sub={c.price_monthly != null ? formatMoney(c.price_monthly) : ''} />
        <Metric label="Subscription" value={c.subscription_status || '—'} />
        <Metric label="Trial ends" value={formatDateShort(c.trial_ends_at)} />
        <Metric label="Period end" value={formatDateShort(c.current_period_end)} />
        <Metric label="PayFast" value={c.has_payfast_token ? 'linked' : 'none'} />
      </div>

      <div className={styles.metricsGrid}>
        <Metric label="Users" value={formatNum(detail.users?.total)} sub={`${detail.users?.verified ?? 0} verified, ${detail.users?.unverified ?? 0} unverified`} />
        <Metric label="Employees" value={formatNum(detail.employees?.total)} sub={`${detail.employees?.real_count ?? 0} real, ${detail.employees?.ghost_count ?? 0} ghost`} />
        <Metric label="Resources" value={formatNum(detail.resources?.total)} />
        <Metric label="Active projects" value={formatNum(detail.projects?.active)} sub={`${detail.projects?.total ?? 0} total`} />
        <Metric label="Overdue projects" value={formatNum(detail.projects?.overdue)} warn={(detail.projects?.overdue ?? 0) > 0} />
        <Metric label="Open leads" value={formatNum(detail.leads?.open)} sub={`Win rate ${detail.leads?.conversion_pct ?? '—'}%`} />
        <Metric label="Timesheet hrs (30d)" value={formatNum(detail.timesheets?.hours_30d)} sub={`${detail.timesheets?.entries_7d ?? 0} entries (7d)`} />
        <Metric label="Booking utilization" value={`${detail.schedule?.utilization_pct ?? 0}%`} />
      </div>

      {/* Projects */}
      <SectionTitle>Projects</SectionTitle>
      <div className={styles.twoCol}>
        <Card title="By status">
          {(detail.projects?.by_status || []).length === 0 ? (
            <div className={styles.empty}>No projects yet.</div>
          ) : (
            <DistBars
              items={(detail.projects?.by_status || []).map((s) => ({
                label: s.status,
                value: s.count,
              }))}
            />
          )}
        </Card>
        <Card title="Top 5 by actual cost">
          <Table
            columns={['Project', 'Client', 'Hours', 'Cost']}
            rows={(detail.projects?.top_spend || []).map((p) => [
              p.project_name,
              p.client_company || '—',
              p.actual_hours,
              formatMoney(p.actual_cost),
            ])}
          />
        </Card>
      </div>
      {(detail.projects?.overdue_list || []).length > 0 && (
        <Card title="Overdue projects">
          <Table
            columns={['Project', 'Client', 'End date']}
            rows={detail.projects.overdue_list.map((p) => [
              p.project_name,
              p.client_company || '—',
              formatDate(p.end_date),
            ])}
          />
        </Card>
      )}

      {/* Leads */}
      <SectionTitle>Leads</SectionTitle>
      <div className={styles.twoCol}>
        <Card title="By status">
          {(detail.leads?.by_status || []).length === 0 ? (
            <div className={styles.empty}>No leads yet.</div>
          ) : (
            <DistBars items={detail.leads.by_status.map((s) => ({ label: s.status, value: s.count }))} />
          )}
        </Card>
        <Card title="By stage (temperature)">
          {(detail.leads?.by_stage || []).length === 0 ? (
            <div className={styles.empty}>No leads yet.</div>
          ) : (
            <DistBars items={detail.leads.by_stage.map((s) => ({ label: s.stage, value: s.count }))} />
          )}
        </Card>
      </div>
      {(detail.leads?.stale || []).length > 0 && (
        <Card title="Stale leads (open > 14 days)">
          <Table
            columns={['Lead', 'Client', 'Status', 'Stage', 'Opened']}
            rows={detail.leads.stale.map((l) => [
              l.lead_name,
              l.client_company || '—',
              l.status,
              l.stage || '—',
              formatDate(l.timestamp_begin),
            ])}
          />
        </Card>
      )}

      {/* Timesheets */}
      <SectionTitle>Timesheets</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Entries total" value={formatNum(detail.timesheets?.entries_total)} />
        <Metric label="Hours total" value={formatNum(detail.timesheets?.hours_total)} />
        <Metric label="Hours (7d)" value={formatNum(detail.timesheets?.hours_7d)} />
        <Metric label="Hours (30d)" value={formatNum(detail.timesheets?.hours_30d)} />
      </div>
      <div className={styles.twoCol}>
        <Card title="Top contributors (30d)">
          {(detail.timesheets?.top_contributors || []).length === 0 ? (
            <div className={styles.empty}>No entries.</div>
          ) : (
            <DistBars
              items={detail.timesheets.top_contributors.map((c) => ({
                label: c.employee_name,
                value: Number(c.hours_30d),
              }))}
            />
          )}
        </Card>
        <Card title="Hours per day (14d)">
          <BarChart series={(detail.timesheets?.by_day || []).map((d) => ({ label: d.date, value: Number(d.hours) }))} />
        </Card>
      </div>

      {/* Schedule */}
      <SectionTitle>Schedule</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Bookings total" value={formatNum(detail.schedule?.total)} />
        <Metric label="Active now" value={formatNum(detail.schedule?.active_now)} />
        <Metric label="Upcoming (7d)" value={formatNum(detail.schedule?.upcoming_7d)} />
        <Metric label="Hours this week" value={formatNum(detail.schedule?.hours_this_week)} />
        <Metric label="Utilization" value={`${detail.schedule?.utilization_pct ?? 0}%`} />
      </div>
      {(detail.schedule?.overallocated || []).length > 0 && (
        <Card title="Over-allocated this week">
          <Table
            columns={['Employee', 'Booked', 'Capacity']}
            rows={detail.schedule.overallocated.map((e) => [
              e.name,
              `${e.booked_hours} h`,
              `${e.weekly_hours} h`,
            ])}
          />
        </Card>
      )}

      {/* Time off */}
      <SectionTitle>Time off</SectionTitle>
      <div className={styles.twoCol}>
        <Card title="Pending requests">
          {(detail.time_off?.pending || []).length === 0 ? (
            <div className={styles.empty}>None pending.</div>
          ) : (
            <Table
              columns={['Employee', 'Type', 'Start', 'End']}
              rows={detail.time_off.pending.map((t) => [
                t.employee_name,
                t.leave_type || '—',
                t.start_date,
                t.end_date,
              ])}
            />
          )}
        </Card>
        <Card title="Upcoming approved (30d)">
          {(detail.time_off?.upcoming || []).length === 0 ? (
            <div className={styles.empty}>None.</div>
          ) : (
            <Table
              columns={['Employee', 'Type', 'Start', 'End']}
              rows={detail.time_off.upcoming.map((t) => [
                t.employee_name,
                t.leave_type || '—',
                t.start_date,
                t.end_date,
              ])}
            />
          )}
        </Card>
      </div>
      {(detail.time_off?.by_type || []).length > 0 && (
        <Card title="All-time requests by type">
          <DistBars items={detail.time_off.by_type.map((t) => ({ label: t.leave_type, value: t.count }))} />
        </Card>
      )}

      {/* Notifications */}
      <SectionTitle>Notifications</SectionTitle>
      <div className={styles.metricsGrid}>
        <Metric label="Total" value={formatNum(detail.notifications?.total)} />
        <Metric label="Unread" value={formatNum(detail.notifications?.unread)} />
        <Metric label="Sent (7d)" value={formatNum(detail.notifications?.sent_7d)} />
        <Metric label="Sent (30d)" value={formatNum(detail.notifications?.sent_30d)} />
      </div>
      {(detail.notifications?.by_type || []).length > 0 && (
        <Card title="Top event types (30d)">
          <DistBars items={detail.notifications.by_type.map((n) => ({ label: n.event_type, value: n.count }))} />
        </Card>
      )}

      {/* Requests */}
      <SectionTitle>API requests (last 7 days)</SectionTitle>
      {requests ? (
        <>
          <div className={styles.metricsGrid}>
            <Metric label="Total" value={formatNum(detail.request_volume_7d?.total)} />
            <Metric label="4xx errors" value={formatNum(detail.request_volume_7d?.errors_4xx)} warn={(detail.request_volume_7d?.errors_4xx ?? 0) > 0} />
            <Metric label="5xx errors" value={formatNum(detail.request_volume_7d?.errors_5xx)} warn={(detail.request_volume_7d?.errors_5xx ?? 0) > 0} />
          </div>
          <Card title="Per-day volume">
            <RequestsTimeline series={requests.by_day || []} />
          </Card>
          <Card title="Top endpoints">
            <Table
              columns={['Method', 'Route', 'Count', '4xx', '5xx', 'Avg ms']}
              rows={(requests.by_endpoint || []).map((r) => [
                r.method,
                r.route,
                formatNum(r.count),
                formatNum(r.errors_4xx),
                formatNum(r.errors_5xx),
                r.avg_ms ?? '—',
              ])}
            />
          </Card>
        </>
      ) : (
        <div className={styles.loading}>No request data.</div>
      )}

      {/* Activity */}
      <SectionTitle>Recent activity</SectionTitle>
      {(detail.activity || []).length === 0 ? (
        <div className={styles.empty}>No activity yet.</div>
      ) : (
        <Table
          columns={['When', 'Type', 'Subject', 'Detail']}
          rows={detail.activity.map((a) => [
            formatDate(a.ts),
            <span className={`${styles.badge} ${styles.badgeMuted}`}>{a.kind.replace(/_/g, ' ')}</span>,
            a.subject || '—',
            a.detail || '—',
          ])}
        />
      )}
    </>
  );
}

/* ---------- Reusable presentational ---------- */

function Metric({ label, value, sub, warn }) {
  return (
    <div className={`${styles.metricCard} ${warn ? styles.metricCardWarn : ''}`}>
      <p className={styles.metricLabel}>{label}</p>
      <p className={styles.metricValue}>{value}</p>
      {sub && <p className={styles.metricSub}>{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className={styles.sectionTitle}>{children}</h2>;
}

function Card({ title, children }) {
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <div className={styles.cardBody}>{children}</div>
    </div>
  );
}

function Table({ columns, rows }) {
  if (!rows.length) return <div className={styles.empty}>No records.</div>;
  return (
    <table className={styles.table}>
      <thead>
        <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

function DistBars({ items }) {
  const max = Math.max(1, ...items.map((i) => Number(i.value) || 0));
  return (
    <div className={styles.distList}>
      {items.map((i) => {
        const v = Number(i.value) || 0;
        return (
          <div key={i.label} className={styles.distRow}>
            <span className={styles.distLabel}>{i.label}</span>
            <div className={styles.distBarTrack}>
              <div className={styles.distBarFill} style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className={styles.distValue}>{formatNum(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

function BarChart({ series }) {
  if (!series.length) return <div className={styles.empty}>No data.</div>;
  const max = Math.max(1, ...series.map((d) => Number(d.value) || 0));
  return (
    <div className={styles.miniChart}>
      <div className={styles.chartBars}>
        {series.map((d) => (
          <div key={d.label} className={styles.chartCol} title={`${d.label}: ${d.value}`}>
            <div className={styles.chartBarStack}>
              <div
                className={`${styles.chartBar} ${styles.barBlue}`}
                style={{ height: `${(Number(d.value) / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.chartXAxis}>
        <span>{series[0]?.label}</span>
        <span>{series[series.length - 1]?.label}</span>
      </div>
    </div>
  );
}
